import { Link, useHistory, useLocation } from "react-router-dom";
import { compile } from "path-to-regexp";
import { MediaQuery } from "@outplayed/responsive";
import { TooltipContainer } from "@outplayed/tooltips";
import { AppRoutes, getClassicChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { OVERVIEW_QUEUE_TYPE_OPTIONS } from "@ugg/shared/query-params/filter-options/champions/overview";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import DefaultSelect from "@ugg/shared/components/FilterManager/DefaultSelect";
import { FilterManagerWrapper } from "@ugg/shared/components/FilterManager/FilterManager";
import { ROLE_OPTIONS, REGION_OPTIONS_ONLY_WORLD } from "@ugg/shared/query-params/filter-options/common";
import { ReactComponent as FilterIcon } from "@ugg/shared/assets/svg/filters-icon.svg";
import { normalizeRole } from "@ugg/shared/utils/role-helpers";

// Part of the League Classic build page. See ./ClassicChampionProfile.tsx for
// the module boundary rules: nothing outside this folder imports from here
// except ./index.ts, and nothing in here is hoisted into shared component
// directories — that is what keeps Classic deletable as one unit.

type ClassicQueueOption = { value: string; label: string; path: string; reset?: string[] };
type ClassicPatchOption = { value: string; label: string };

export function ClassicFilterBar(props: {
  className?: string;
  role: string;
  // The roles Classic has tier-list data for, derived per champion by the page.
  // Empty while the tier list is still resolving (and if the champion has no
  // entry at all) — the role slot renders blank rather than guessing.
  availableRoles: string[];
  alias: string;
  // Real patch window (newest N patches, matching the tier list). `selectedPatch`
  // is the active raw value ("16_14"); `latestPatch` is the newest raw value —
  // selecting it drops the `?patch=` param so the canonical URL stays clean.
  patchOptions: ClassicPatchOption[];
  selectedPatch: string;
  latestPatch?: string;
}) {
  const { className, role, availableRoles, alias, patchOptions, selectedPatch, latestPatch } = props;
  const roleOptions = ROLE_OPTIONS.filter((option) => availableRoles.includes(normalizeRole(option.value)));
  const history = useHistory();
  const location = useLocation();

  // Queue selector — the same game-mode options as the normal build page, plus a
  // "Classic" entry for the current mode. Selecting another mode navigates to
  // that mode's build page for this champion, compiling the option's route with
  // the champion alias (mirroring the FilterManager's path handling) and carrying
  // queueType as a query param unless the option resets it (special modes do).
  const queueOptions: ClassicQueueOption[] = [
    { value: "classic", label: "Classic", path: AppRoutes.CHAMPION_PROFILE_BUILD_CLASSIC },
    ...OVERVIEW_QUEUE_TYPE_OPTIONS,
  ];

  const onQueueChange = (option: ClassicQueueOption) => {
    if (option.value === "classic") return; // already on the Classic page
    const pathname = compile(option.path, { encode: encodeURIComponent })({ champion: alias });
    const params: Record<string, string> = { queueType: option.value };
    (option.reset || []).forEach((key) => delete params[key]);
    history.push({ pathname, search: buildQueryParams(params) });
  };

  // Patch selector — set/clear the `?patch=` query param while keeping the
  // current pathname (role route + champion). Selecting the latest patch drops
  // the param (latest is canonical, no param); older patches set it. Other
  // query params are preserved.
  const onPatchChange = (option: ClassicPatchOption) => {
    if (option.value === selectedPatch) return;
    const search = new URLSearchParams(location.search);
    if (!latestPatch || option.value === latestPatch) search.delete("patch");
    else search.set("patch", option.value);
    const qs = search.toString();
    history.push({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
  };

  return (
    <FilterManagerWrapper className={className}>
      <div className="filter-manager_label">
        <FilterIcon />
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <span>Filters</span>
        </MediaQuery>
      </div>
      <div className="flex-1 flex items-center flex-wrap gap-[12px]">
        {/* Role split — same markup/CSS as the normal build page's
            ChampionOverviewRoleSplit, but showing only the roles Classic has
            data for instead of all five.

            Classic's build feeds (runes / spells / items / skills / masteries)
            are keyed on champion + patch alone, with no role axis, so offering
            the other four roles only ever swapped the label while the build
            stayed put. The tier list lists each champion under exactly one
            role today, which makes this a single non-interactive icon — a
            statement of the champion's Classic role rather than a filter.

            Nothing is rendered until `availableRoles` arrives: the app has no
            SSR, so the tier list lands a beat after first paint and guessing
            would show the wrong role and then swap. The empty cell below
            holds the 36px slot so the selects beside it don't jump.

            The active role is a div, not a self-linking Link: with one role
            that link would only regenerate the /…-classic/<role> URLs this
            change retires. Non-active roles stay Links so a future
            multi-role champion is navigable with no further work. */}
        <div className="role-filter-container">
          {roleOptions.length === 0 ? (
            <div className="role-filter pointer-events-none" aria-hidden />
          ) : (
            roleOptions.map((option) => {
              const isSelected = normalizeRole(role) === normalizeRole(option.value);
              const icon = isSelected && option.imgActive ? option.imgActive : option.img;
              return (
                <TooltipContainer key={option.value} tooltipInfo={option.label}>
                  {isSelected ? (
                    <div className="role-filter active !cursor-default">{icon}</div>
                  ) : (
                    <Link className="role-filter" to={getClassicChampionBuildUrl(alias, { role: option.value })}>
                      {icon}
                    </Link>
                  )}
                </TooltipContainer>
              );
            })
          )}
        </div>
        {/* Queue selector — switch to any other game mode's build for this
            champion (Classic is the current selection). */}
        <DefaultSelect
          className="filter-select queue-type"
          options={queueOptions}
          value="classic"
          onChange={onQueueChange}
          formatOptionLabel={(o) => <div>{o.label}</div>}
          width={150}
          isSearchable={false}
        />
        {/* Patch selector — the newest N Classic patches (same window as the
            tier list). Changing it drives the `?patch=` query param. */}
        <DefaultSelect
          className="filter-select"
          options={patchOptions}
          value={selectedPatch}
          onChange={onPatchChange}
          width={110}
          isSearchable={false}
        />
        {/* Worlds-only region (matches the ARAM Mayhem augment page). */}
        <DefaultSelect
          className="filter-select"
          options={REGION_OPTIONS_ONLY_WORLD}
          value="world"
          onChange={() => {}}
          formatOptionLabel={({ label, img, flag }) => (
            <div className="region-option">
              <div className="region-img-wrapper">
                <img className={`region-img ${flag ? "flag-img" : ""}`} src={img as string} />
              </div>
              <span style={{ marginLeft: "10px" }}>{label}</span>
            </div>
          )}
          width={120}
          isSearchable={false}
        />
      </div>
    </FilterManagerWrapper>
  );
}

// ─── Recommended panel (tab + auto-import + warning; runes/spells placeholders) ─
